import { useState } from 'react';
import { Close, Copy, Checkmark, Renew, Information } from '@carbon/icons-react';
import { useUserDebugData, useInvalidateUser } from '@/hooks/use-current-user-org';
import { cn } from '@sharpe/ui/utils';

interface UserDebugOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

function UserDebugOverlay({ isOpen, onClose }: UserDebugOverlayProps) {
  const debugData = useUserDebugData();
  const invalidateUser = useInvalidateUser();
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRefresh = () => {
    invalidateUser();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background shadow-xl w-[90vw] max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-medium">User & Organization Debug</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Refresh data"
            >
              <Renew size={14} />
            </button>
            <button
              onClick={handleCopy}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Copy to clipboard"
            >
              {copied ? <Checkmark size={14} className="text-success" /> : <Copy size={14} />}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <Close size={16} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <DebugSection title="Session (Better Auth)" data={debugData.session} />
          <DebugSection title="User (auth.me)" data={debugData.authMe} />
          <DebugSection title="Organizations" data={debugData.organizations} />
          <DebugSection title="Derived Permissions" data={debugData.permissions} />
          <DebugSection title="Cache Metadata" data={debugData.meta} />
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border text-xs text-muted-foreground">
          Last updated: {debugData.meta.timestamp}
        </div>
      </div>
    </div>
  );
}

interface DebugSectionProps {
  title: string;
  data: unknown;
}

function DebugSection({ title, data }: DebugSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2 bg-muted/30 text-left text-sm font-medium flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <span>{title}</span>
        <span className="text-xs text-muted-foreground">{isExpanded ? '▼' : '▶'}</span>
      </button>
      {isExpanded && (
        <div className="p-3 bg-muted/10">
          <pre className="text-xs font-mono whitespace-pre-wrap break-all overflow-x-auto text-muted-foreground">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

interface UserDebugTriggerProps {
  className?: string;
}

export function UserDebugTrigger({ className }: UserDebugTriggerProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'w-full px-3 py-2 rounded-md text-left text-sm hover:bg-muted flex items-center gap-2',
          className
        )}
      >
        <Information size={14} />
        View Debug Info
      </button>
      <UserDebugOverlay isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}
